import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Menu, Sparkle, X } from "lucide-react";
import { EASE } from "@/components/shared/motion";
import { NavLinks, NavMobileSheet } from "@/components/nav/NavParts";
import { track, EVENTS } from "@/lib/analytics";

const HEADER_INITIAL = { y: -80, opacity: 0 };
const HEADER_ANIMATE = { y: 0, opacity: 1 };

export default function SiteNav({ delay = 0 }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const headerTransition = useMemo(() => ({ duration: 1, delay, ease: EASE }), [delay]);

  const onStart = () => {
    setOpen(false);
    track(EVENTS.START_PROJECT_CLICK, { from: pathname });
  };

  return (
    <motion.header
      data-testid="main-nav"
      className="fixed inset-x-0 top-5 z-[70] mx-auto w-[min(94vw,72rem)]"
      initial={HEADER_INITIAL}
      animate={HEADER_ANIMATE}
      transition={headerTransition}
    >
      <nav
        aria-label="Primary"
        className="flex items-center justify-between gap-5 rounded-full border border-white/10 bg-[#12141C]/60 px-5 py-3 shadow-[0_8px_32px_rgba(0,0,0,0.45)] backdrop-blur-2xl md:px-7 lg:gap-8"
      >
        <Link to="/" data-testid="nav-brand" className="flex shrink-0 items-center gap-2.5">
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#06B6D4]">
            <Sparkle className="h-3.5 w-3.5 text-[#0B0C10]" strokeWidth={2.5} />
          </span>
          <span className="font-display text-sm font-bold uppercase tracking-[0.3em] text-[#F4F6FB]">
            Elentara
          </span>
        </Link>

        <NavLinks />

        <div className="flex items-center gap-3">
          <Link
            to="/start-a-project"
            data-testid="nav-start-project-button"
            onClick={onStart}
            className="group relative hidden shrink-0 overflow-hidden whitespace-nowrap rounded-full border border-[#8B5CF6]/50 bg-[#8B5CF6]/10 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[#F4F6FB] transition-colors duration-300 hover:bg-[#8B5CF6]/25 sm:inline-flex"
          >
            <span className="relative z-10">Start a Project</span>
            <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
          </Link>
          <button
            type="button"
            data-testid="nav-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-[#F4F6FB]/70 transition-colors duration-300 hover:border-[#8B5CF6]/50 hover:text-[#F4F6FB] lg:hidden"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </nav>

      <NavMobileSheet open={open} onClose={() => setOpen(false)} onStart={onStart} />
    </motion.header>
  );
}
